import type { TranslationKey } from "../i18n/translation";

const DESCRIPTION_KEYS: Record<string, TranslationKey> = {
  "This page is out of date. Please reload it and read the current consent text before sending the request.":
    "error.consent_outdated",
  "This account has no active customer profile.": "error.portal_no_account",
  "This customer's portal access has been withdrawn.":
    "error.portal_no_account",
  "The case was not found.": "error.case_not_found",
  "That case does not belong to this customer.": "error.case_not_found",
  "The customer was not found.": "error.portal_no_account",
  "The document was not found.": "error.document_not_found",
  "That version of the document was not found.": "error.document_not_found",
  "The document's contents are not available.": "error.document_not_found",
  "The signature request was not found.": "error.signature_not_found",
  "Slots are not offered that far ahead.": "error.booking_date_unavailable",
  "Slots cannot be requested for a date that has already passed.":
    "error.booking_date_unavailable",
  "That consultant is not taking bookings. Please choose another or let us assign one.":
    "booking.specialist_unavailable",
  "A free consultation is held in person only.": "booking.s1.free_in_person",
  "Draw your signature before signing.": "error.signature_not_drawn",
  "The signature has too many strokes. Clear it and draw it again.":
    "error.signature_too_many_strokes",
  "The signature does not fit the signing pad.": "error.signature_off_pad",
  "The signature could not be verified.": "error.ecp_invalid",
  "The signature must be made by one certificate.": "error.ecp_invalid",
  "Choose your signing certificate rather than your authentication certificate.":
    "error.ecp_wrong_certificate",
  "The certificate was not valid at the time of signing.":
    "error.ecp_certificate_expired",
  "This certificate has been revoked.": "error.ecp_certificate_revoked",
  "This certificate belongs to a different person.": "error.ecp_iin_mismatch",
  "The sign-in details are incorrect.": "error.invalid_credentials",
  "The account is temporarily locked. Try again later.": "error.locked_out",
  "This invitation link is invalid or has expired. Ask your adviser for a new one.":
    "error.invalid_invitation",

  // The registration form's three conflicts. See the note above about why these are said at all.
  "Another customer already has this phone number.":
    "error.phone_already_in_use",
  "Another customer already has this IIN.": "error.iin_already_in_use",
  "An account with this email address already exists.":
    "error.email_already_in_use",

  // The portal's IIN box. Worded apart from the registration conflict above, which tells the
  // visitor to sign in with the number — advice for somebody who already has.
  "Your IIN has already been recorded. Contact your adviser if it is wrong.":
    "error.portal_iin_fixed",
  "This IIN is recorded on another account. Contact your adviser.":
    "error.portal_iin_in_use",

  // The booking wizard's four. All are conflicts or validation failures the customer can act on,
  // which is why their descriptions reach the client at all: a `Failure` would have been a 500
  // and put the reason in the log instead.
  "That time has just been taken. Please choose another.": "error.slot_taken",
  "That time is not on offer. Please choose one of the times shown.":
    "error.slot_not_offered",
  "Online booking is closed at the moment. Please call us instead.":
    "error.booking_closed",
  "This consultation type does not exist or is no longer offered.":
    "error.service_unavailable",

  // Signing in the portal. Each is a request that changed while the page was open, and each says
  // what the customer can do about it — which is usually to ask their adviser.
  "This request has expired. Ask for a new one.": "error.signature_expired",
  "This request is no longer open for signing.": "error.signature_closed",
  "You have already signed this request.": "error.signature_already_signed",
  "The documents changed since this request was issued. Please reopen it.":
    "error.signature_package_changed",
  "This party must sign with an ЭЦП, so a handwritten signature cannot be recorded for them.":
    "error.signature_ecp_required",
  "This party is recorded as signing by hand, so an ЭЦП cannot be accepted for them.":
    "error.signature_manual_required",

  // The ЭЦП path's three failures of our own. None is the customer's to fix, so all say to try again.
  "The signing service is not responding. Please try again shortly.":
    "error.ecp_service_unavailable",
  "A trusted timestamp could not be obtained. Please try again shortly.":
    "error.ecp_service_unavailable",
  "The certificate's revocation status could not be checked. Please try again shortly.":
    "error.ecp_service_unavailable",
};

export function describeServerMessage(
  message: string,
): TranslationKey | undefined {
  return Object.hasOwn(DESCRIPTION_KEYS, message)
    ? DESCRIPTION_KEYS[message]
    : undefined;
}
